export const RESIZE_STORAGE_PREFIX = 'railPageShell:widths:v1:'
export const RAIL_MIN_3C = 180
export const RAIL_MIN_2C = 240
export const SIDECAR_MIN = 240
export const STAGE_MIN = 360
export const SIDE_MAX_FRACTION = 0.45
export const COLLAPSED_RAIL_PX = 64
export const EXPAND_DRAG_PX = 48
export const KEY_STEP = 16
export const KEY_STEP_LARGE = 64

export type ShellWidths = {
  left?: number
  right?: number
}

const isValidWidth = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 64 && value <= 4000

export const readWidths = (key: string | null): ShellWidths => {
  if (!key) return {}
  try {
    if (typeof window === 'undefined' || !window.localStorage) return {}
    const raw = window.localStorage.getItem(RESIZE_STORAGE_PREFIX + key)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }
    const result: ShellWidths = {}
    if (isValidWidth(parsed.left)) {
      result.left = parsed.left
    }
    if (isValidWidth(parsed.right)) {
      result.right = parsed.right
    }
    return result
  } catch {
    return {}
  }
}

export const writeWidths = (key: string | null, widths: ShellWidths): void => {
  if (!key) return
  try {
    if (typeof window === 'undefined' || !window.localStorage) return
    const cleaned: ShellWidths = {}
    if (isValidWidth(widths?.left)) {
      cleaned.left = widths.left
    }
    if (isValidWidth(widths?.right)) {
      cleaned.right = widths.right
    }
    if (cleaned.left === undefined && cleaned.right === undefined) {
      window.localStorage.removeItem(RESIZE_STORAGE_PREFIX + key)
    } else {
      window.localStorage.setItem(RESIZE_STORAGE_PREFIX + key, JSON.stringify(cleaned))
    }
  } catch {
    /* private-window / disabled storage / quota */
  }
}

export type ClampSideOptions = {
  side: 'left' | 'right'
  raw: number
  shellInner: number
  other: number
  gap: number
  variant: 'two-column' | 'three-column'
}

export type ClampSideResult = {
  width: number
  atMin: boolean
  atMax: boolean
}

export const clampSide = (opts: ClampSideOptions): ClampSideResult => {
  const min =
    opts.side === 'left'
      ? opts.variant === 'three-column'
        ? RAIL_MIN_3C
        : RAIL_MIN_2C
      : SIDECAR_MIN

  const gaps = opts.gap * (opts.variant === 'three-column' ? 2 : 1)
  const other = opts.variant === 'two-column' ? 0 : opts.other
  const max = Math.min(
    SIDE_MAX_FRACTION * opts.shellInner,
    opts.shellInner - other - STAGE_MIN - gaps
  )

  if (max < min) {
    return { width: min, atMin: true, atMax: true }
  }

  if (opts.raw <= min) {
    return { width: min, atMin: true, atMax: min === max }
  }

  if (opts.raw >= max) {
    return { width: max, atMin: min === max, atMax: true }
  }

  return { width: opts.raw, atMin: false, atMax: false }
}

export const shouldSnapCollapse = (raw: number, min: number): boolean => raw < min / 2

export const shouldSnapExpand = (dragDx: number): boolean => dragDx >= EXPAND_DRAG_PX

export const keyboardDelta = (
  key: string,
  shift: boolean,
  side: 'left' | 'right'
): number | 'min' | 'max' | null => {
  if (key === 'Home') return 'min'
  if (key === 'End') return 'max'

  const step = shift ? KEY_STEP_LARGE : KEY_STEP
  if (key === 'ArrowRight') {
    return side === 'left' ? step : -step
  }
  if (key === 'ArrowLeft') {
    return side === 'left' ? -step : step
  }

  return null
}
